import { computed, inject, Injector, Type } from '@angular/core';

import {
  patchState,
  signalStore,
  withComputed,
  withFeature,
  withMethods,
  withProps,
  withState,
} from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { tapResponse } from '@ngrx/operators';

import { ShallowCamelCasePipe } from '@shared/pipes/shallow-camel-case.pipe';
import { WcgTableUtilsService } from '@shared/services/wcg-table-utils.service';

import {
  LoadGridPayload,
  OrganizationSite,
  SiteGridTypes,
  SiteListSignalState,
} from '../../types';
import {
  CONTACT_URL,
  ORGANIZATION_SITE_GRID_STATE,
  SITE_URL,
  STUDY_URL,
  siteListInitialState,
} from '../../constants';

import {
  BuildLinkFromValue,
  GridColumnCasePipe,
} from '@wcg/frontend-framework';
import { map, pipe, switchMap, tap } from 'rxjs';
import { withOrganizationSiteEntities } from '../entities';

import { SiteListService } from '../../services';
import { withGridConfigFeatures } from '../features';
import { transformGridDateFormat } from '@data-access/shared/utils';

export const ORGANIZATION_SITE_LIST_SIGNAL_STORE = signalStore(
  withState(() => inject(ORGANIZATION_SITE_GRID_STATE)),
  withProps((store, _siteListService = inject(SiteListService)) => ({
    _gridCasePipe: new GridColumnCasePipe(),
    _siteListService,
    _shallowCamelCasePipe: new ShallowCamelCasePipe(),
    _buildLinkFromValue: new BuildLinkFromValue(),
    _wcgTableUtilService: inject(WcgTableUtilsService),
    _injector: inject(Injector),
    store,
  })),
  withFeature(() => withGridConfigFeatures()),
  withFeature(() => withOrganizationSiteEntities()),
  withComputed(
    ({
      error,
      _siteEntities,
      total,
      isLoading,
      currentUrl,
      gridType,
      store,
      _injector,
    }) => ({
      $currentUrl: computed(() => currentUrl()),
      $error: computed(() => error()),
      $gridType: computed(() => gridType()),
      $isLoading: computed(() => isLoading()),
      $sites: computed(() => {
        const _sites = _siteEntities();
        if (!_sites) {
          return [] as OrganizationSite[];
        }
        return transformGridDateFormat<OrganizationSite>(
          _injector,
          _sites,
          store.columnsCopy(),
        );
      }),
      $totalCount: computed(() => total()),
    }),
  ),
  withMethods(
    ({
      _siteListService,
      _shallowCamelCasePipe,
      _buildLinkFromValue,
      $currentUrl,
      ...store
    }) => {
      const _loadSiteList = rxMethod<LoadGridPayload>(
        pipe(
          tap(() => patchState(store, { isLoading: true })),
          switchMap(({ params, updateColumns }) =>
            _siteListService.getSiteList($currentUrl(), params).pipe(
              map((response) => ({
                ...response,
                config: _shallowCamelCasePipe.transform(response.config),
              })),
              tapResponse({
                error: (error: Error) =>
                  patchState(store, { error: error, isLoading: false }),
                finalize: () => patchState(store, { isLoading: false }),
                next: (response) => {
                  patchState(store, {
                    currentStateDestroyed: false,
                    total: response.total,
                  });
                  store.loadSites(response.data);
                  store.loadConfigurations(
                    response.config,
                    updateColumns,
                    response.data?.[0],
                    _buildLinkFromValue,
                  );
                  store.setGridViews(response.views);
                },
              }),
            ),
          ),
        ),
      );

      const _getGridMetaData = (
        type: string,
        entityId?: string,
        isPinned?: boolean,
      ): {
        currentUrl: string;
      } => {
        switch (type) {
          case 'organization':
            return {
              currentUrl: `${SITE_URL}organization/${entityId}/`,
            };
          case 'main':
            return {
              currentUrl: isPinned
                ? `${SITE_URL}?is_pinned=true`
                : `${SITE_URL}`,
            };
          case 'contact':
            return {
              currentUrl: `${CONTACT_URL}/${entityId}/sites/`,
            };
          case 'study':
            return {
              currentUrl: `${STUDY_URL}${entityId}/site/`,
            };
          default:
            throw new Error('Invalid type for loading site grid');
        }
      };

      const _setGridMetaData = (
        type: SiteGridTypes,
        entityId?: string,
        isPinned?: boolean,
      ) => {
        const { currentUrl } = _getGridMetaData(type, entityId, isPinned);
        patchState(store, { currentUrl });
      };

      return {
        loadSiteGrid: (payload: LoadGridPayload) => {
          return _loadSiteList(payload);
        },
        resetGridSettings: (payload: LoadGridPayload) => {
          _loadSiteList(payload);
          patchState(store, { searchString: '' });
          store.setDefaultGridView();
        },
        resetState: () =>
          patchState(store, {
            ...siteListInitialState,
            currentStateDestroyed: true,
          }),
        setByURL: (
          type: SiteGridTypes,
          entityId?: string,
          isPinned?: boolean,
        ) => _setGridMetaData(type, entityId, isPinned),
      };
    },
  ),
) satisfies Type<SiteListSignalState>;
